import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {D} from './design-tokens';

// ── Types ─────────────────────────────────────────────────────────────────────

export type StatusCardRow = {
	projectName: string;
	projectUrl?: string;
	severity?: string;
	tasksOverdue?: string;
	daysPastDeadline?: string;
	client?: string;
	status?: string;
	date?: string;
};

// ── Accent colour ─────────────────────────────────────────────────────────────

function deriveAccentColor(row: StatusCardRow): string {
	const status = (row.status ?? '').toLowerCase();
	if (/\bend\b/.test(status) || status.includes('at risk') || status.includes('red')) return D.v2.statusRed;
	if (status.includes('warn') || status.includes('amber') || status.includes('orange')) return D.v2.statusAmber;
	if (status.includes('on track') || status.includes('green')) return D.v2.statusGreen;
	const sev = Number(row.severity?.replace(/[^0-9.]/g, ''));
	if (!isNaN(sev) && sev > 0) {
		if (sev >= 70) return D.v2.statusRed;
		if (sev >= 40) return D.v2.statusAmber;
		return D.v2.statusGreen;
	}
	return D.textDisabled;
}

// ── Styled components ─────────────────────────────────────────────────────────

const CardsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 14px;
	margin-bottom: 14px;
`;

const Card = styled.div`
	display: flex;
	background: ${D.bg};
	border: 1px solid ${D.inputBorder};
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 1px 2px 0 rgba(35, 35, 52, 0.06), 0 1px 1px 0 rgba(35, 35, 52, 0.04);
`;

const AccentBar = styled.div<{color: string}>`
	width: 4px;
	flex-shrink: 0;
	background: ${({color}) => color};
`;

const CardContent = styled.div`
	flex: 1;
	padding: 14px 16px;
	min-width: 0;
`;

const TitleRow = styled.div`
	display: flex;
	align-items: baseline;
	gap: 12px;
	margin-bottom: 6px;
`;

const ProjectLink = styled.a`
	font-size: 16px;
	font-weight: 500;
	color: ${D.v2.accent};
	text-decoration: none;
	cursor: pointer;
	flex-shrink: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&:hover {
		text-decoration: underline;
		text-decoration-color: ${D.v2.accent};
	}
`;

const ProjectName = styled.span`
	font-size: 16px;
	font-weight: 500;
	color: ${D.v2.textPrimary};
	flex-shrink: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const SeverityLabel = styled.span`
	font-size: 12px;
	font-weight: 350;
	color: ${D.v2.textSecondary};
	white-space: nowrap;
	flex-shrink: 0;
`;

const StatsRow = styled.div`
	font-size: 14px;
	font-weight: 350;
	color: ${D.v2.textPrimary};
	margin-bottom: 8px;
	line-height: 20px;
`;

const StatsNumber = styled.strong`
	font-weight: 700;
`;

const MetaRow = styled.div`
	display: flex;
	gap: 8px;
	font-size: 12px;
	font-weight: 350;
	color: ${D.v2.textSecondary};
	line-height: 16px;
	flex-wrap: wrap;
`;

const MetaDot = styled.span`
	color: ${D.textDisabled};
`;

// ── Table parsing ─────────────────────────────────────────────────────────────

const STATUS_CARD_KEYWORDS = [
	'project',
	'name',
	'severity',
	'tasks',
	'days',
	'status',
	'client',
	'company',
	'overdue',
	'deadline',
];

/** Extracts plain text from a React node (recursive). */
function nodeText(node: React.ReactNode): string {
	if (node == null) return '';
	if (typeof node === 'string') return node;
	if (typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(nodeText).join('');
	if (React.isValidElement(node)) {
		return nodeText((node.props as {children?: React.ReactNode}).children);
	}
	return '';
}

/**
 * Attempts to parse a ReactMarkdown `table` children structure into StatusCardRow[].
 * Returns null if the table headers don't match the status-card pattern.
 */
export function tryParseStatusCardTable(children: React.ReactNode): StatusCardRow[] | null {
	const childArray = React.Children.toArray(children);

	// Find thead and tbody elements
	const thead = childArray.find(
		c =>
			React.isValidElement(c) &&
			(c.type === 'thead' || (c.props as {node?: {tagName?: string}}).node?.tagName === 'thead')
	);
	const tbody = childArray.find(
		c =>
			React.isValidElement(c) &&
			(c.type === 'tbody' || (c.props as {node?: {tagName?: string}}).node?.tagName === 'tbody')
	);

	if (!thead || !tbody) return null;

	// Extract header texts
	const theadEl = thead as React.ReactElement<{children?: React.ReactNode}>;
	const headerRow = React.Children.toArray(theadEl.props.children)[0];
	if (!React.isValidElement(headerRow)) return null;

	const headerCells = React.Children.toArray((headerRow as React.ReactElement<{children?: React.ReactNode}>).props.children);
	const headers = headerCells.map(cell => nodeText(cell).toLowerCase().trim());

	// Require at least this many header matches to treat the table as a status-card table
	const MIN_KEYWORD_MATCHES = 2;
	const matchCount = headers.filter(h => STATUS_CARD_KEYWORDS.some(kw => h.includes(kw))).length;
	if (matchCount < MIN_KEYWORD_MATCHES) return null;

	// Map header index → field name
	function headerIndex(keywords: string[]): number {
		return headers.findIndex(h => keywords.some(kw => h.includes(kw)));
	}

	const iProject = headerIndex(['project', 'name']);
	const iSeverity = headerIndex(['severity', 'score', 'risk score']);
	const iTasks = headerIndex(['task', 'overdue']);
	const iDays = headerIndex(['day', 'deadline', 'past']);
	const iClient = headerIndex(['client', 'company', 'account']);
	const iStatus = headerIndex(['status', 'health', 'stage']);
	const iDate = headerIndex(['date', 'end', 'due']);

	// Parse tbody rows
	const tbodyEl = tbody as React.ReactElement<{children?: React.ReactNode}>;
	const rows: StatusCardRow[] = [];

	React.Children.forEach(tbodyEl.props.children, rowNode => {
		if (!React.isValidElement(rowNode)) return;
		const cells = React.Children.toArray((rowNode as React.ReactElement<{children?: React.ReactNode}>).props.children);

		function cellText(idx: number): string | undefined {
			if (idx < 0 || idx >= cells.length) return undefined;
			const t = nodeText(cells[idx]).trim();
			return t || undefined;
		}

		// Try to extract a URL from the project name cell by walking all descendants
		let projectUrl: string | undefined;
		function findHref(node: React.ReactNode): string | undefined {
			if (!React.isValidElement(node)) return undefined;
			const el = node as React.ReactElement<{href?: string; children?: React.ReactNode}>;
			if (el.props.href) return el.props.href;
			for (const child of React.Children.toArray(el.props.children)) {
				const found = findHref(child);
				if (found) return found;
			}
			return undefined;
		}
		if (iProject >= 0 && iProject < cells.length) {
			projectUrl = findHref(cells[iProject]);
		}

		const projectName = cellText(iProject);
		if (!projectName) return;

		rows.push({
			projectName,
			projectUrl,
			severity: cellText(iSeverity),
			tasksOverdue: cellText(iTasks),
			daysPastDeadline: cellText(iDays),
			client: cellText(iClient),
			status: cellText(iStatus),
			date: cellText(iDate),
		});
	});

	return rows.length > 0 ? rows : null;
}

// ── Component ─────────────────────────────────────────────────────────────────

type StatusCardsProps = {
	rows: StatusCardRow[];
	history?: {push: (path: string) => void} | null;
};

export function safeProjectUrl(url: string | undefined): string | undefined {
	if (!url) return undefined;
	if (/^https?:\/\//i.test(url)) return url; // absolute external link
	if (url.startsWith('/')) return url; // relative in-app link (e.g. /project/P42)
	return undefined;
}

export function StatusCards({rows, history}: StatusCardsProps): JSX.Element {
	const {formatMessage} = useIntl();
	return (
		<CardsList>
			{rows.map((row, i) => {
				const accentColor = deriveAccentColor(row);
				const projectUrl = safeProjectUrl(row.projectUrl);
				const isInternal = !!projectUrl && projectUrl.startsWith('/');
				return (
					<Card key={`${row.projectName ?? ''}-${i}`}>
						<AccentBar color={accentColor} />
						<CardContent>
							<TitleRow>
								{projectUrl ? (
									<ProjectLink
										href={projectUrl}
										title={row.projectName}
										{...(isInternal
											? {
													onClick: (e: React.MouseEvent) => {
														// In-app SPA nav (no full reload), matching the
														// markdown internal-link behaviour.
														e.preventDefault();
														if (history) history.push(projectUrl);
													},
											  }
											: {target: '_blank', rel: 'noopener noreferrer'})}
									>
										{row.projectName}
									</ProjectLink>
								) : (
									<ProjectName title={row.projectName}>{row.projectName}</ProjectName>
								)}
								{row.severity && (
									<SeverityLabel>
										{formatMessage({id: 'forecast_agent.status_card.severity'}, {severity: row.severity})}
									</SeverityLabel>
								)}
							</TitleRow>

							{(row.tasksOverdue || row.daysPastDeadline) && (
								<StatsRow>
									{row.tasksOverdue && (
										<>
											<StatsNumber>{row.tasksOverdue}</StatsNumber>
											{formatMessage({id: 'forecast_agent.status_card.tasks_overdue'})}
										</>
									)}
									{row.tasksOverdue && row.daysPastDeadline && ' · '}
									{row.daysPastDeadline && (
										<>
											<StatsNumber>{row.daysPastDeadline}</StatsNumber>
											{formatMessage({id: 'forecast_agent.status_card.days_past_deadline'})}
										</>
									)}
								</StatsRow>
							)}

							{(row.client || row.status || row.date) && (
								<MetaRow>
									{row.client && <span>{row.client}</span>}
									{row.client && (row.status || row.date) && <MetaDot>·</MetaDot>}
									{row.status && row.date ? (
										<span>
											{row.status} {row.date}
										</span>
									) : row.status ? (
										<span>{row.status}</span>
									) : row.date ? (
										<span>{row.date}</span>
									) : null}
								</MetaRow>
							)}
						</CardContent>
					</Card>
				);
			})}
		</CardsList>
	);
}
